// Copyright (c) 2022 by Move Closer

import { ComponentInternalInstance, computed, PropType } from '@vue/composition-api'

import { ComponentObjectPropsOptions } from '../../vue-api'

import { useOptionClass } from '../../composables'

import {
  ContainerBackground,
  ContainerBackgroundImagePosition,
  ContainerMargin,
  ContainerOffset,
  ContainerPadding,
  ContainerProps,
  ContainerWidth,
  UseContainerProvides
} from './Container.contracts'

import {
  containerColorRegistry,
  containerColumnGap,
  containerMarginClassNameRegistry,
  containerPaddingClassNameRegistry,
  containerRowGap,
  containerWidthClassNameRegistry,
  containerZIndexClassNameRegistry
} from './Container.config'

import { AnyObject } from '@movecloser/front-core'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const containerProps: ComponentObjectPropsOptions<ContainerProps> = {
  background: {
    type: Object as PropType<Partial<ContainerBackground>>,
    required: false,
    default: () => ({})
  },

  color: {
    type: String,
    required: false,
    default: ''
  },

  gridGap: {
    type: Object,
    required: false
  },

  id: {
    type: String,
    required: false,
    default: () => `a${Math.floor(Math.random() * 10000)}`
  },

  margin: {
    type: Object as PropType<ContainerOffset>,
    required: false
  },

  padding: {
    type: Object,
    required: false
  },

  innerClass: {
    type: String,
    required: false,
    default: ''
  },

  width: {
    type: String,
    required: false,
    default: ContainerWidth.Normal
  },

  zIndex: {
    type: Number,
    required: false
  }
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const useContainer = (
  props: ContainerProps,
  instance: ComponentInternalInstance | null
): UseContainerProvides => {
  if (instance === null) {
    throw new Error('useContainer(): FATAL ERROR! Failed to resolve the component instance!')
  }

  const hasBackground = computed<boolean>(() => {
    return typeof props.background !== 'undefined'
  })

  const hasBackgroundImage = computed<boolean>(() => {
    return typeof props.background !== 'undefined' &&
      typeof props.background.image !== 'undefined' && ('src' in props.background.image)
  })

  const hasFixedBackgroundImage = computed<boolean>(() => {
    return props.background.imagePosition === ContainerBackgroundImagePosition.Fixed
  })

  const fixedBackgroundStyles = computed<AnyObject>(() => {
    if (!props.background.image) {
      return {}
    }

    const srcset = Object.entries(props.background.image.srcset ?? {})
    const [, largestImage] = srcset[srcset.length - 1]

    return {
      background: `url(${largestImage}) center / cover fixed`
    }
  })

  const hasStyleDefined = computed<boolean>(() => {
    return (props.color && props.color.length > 0) || (typeof props.gridGap === 'object' && props.gridGap !== null)
  })

  /**
   * Determines position of the image in container.
   */
  const bgImagePosition = computed<string>(() => {
    if (!hasBackground) {
      return ContainerBackgroundImagePosition.Center
    }

    if (
      typeof props.background.imagePosition !== 'string' ||
      !(Object.values(ContainerBackgroundImagePosition).includes(props.background.imagePosition as ContainerBackgroundImagePosition))
    ) {
      return ContainerBackgroundImagePosition.Center
    }

    return props.background.imagePosition
  })

  /**
   * Return inner color class definition.
   */
  const stylesDefinition = computed<string>(() => {
    const prefix: string = `#c_${props.id}`

    let styles: string = ''
    if (props.color) {
      const candidate: string = props.color
      const color: string | undefined = candidate.startsWith('#') ? candidate : containerColorRegistry[candidate]
      if (color) {
        styles = styles.concat(`${prefix} * { color: ${color} }`)
      }
    }

    if (props.gridGap) {
      let gridGapStyle: string = ''
      const columnValue: string | null = containerColumnGap[props.gridGap.column]
      if (columnValue && columnValue.length > 0) {
        gridGapStyle = `grid-column-gap: ${columnValue};`
      }

      const rowValue: string | null = containerRowGap[props.gridGap.row]
      if (rowValue && rowValue.length > 0) {
        gridGapStyle = `${gridGapStyle}grid-row-gap: ${rowValue};`
      }

      if (gridGapStyle.length > 0) {
        styles = styles.concat(`${prefix} .viewer__container-inner { ${gridGapStyle} }`)
      }
    }

    return styles
  })

  /**
   * CSS class that determines the container's vertical margin.
   */
  const marginClassName = computed<string>(() => {
    if (!props.margin) {
      return ''
    }

    const bottomMarginClassName: string =
      containerMarginClassNameRegistry[props.margin.bottom].bottom

    const topMarginClassName: string =
      containerMarginClassNameRegistry[props.margin.top].top

    return `${bottomMarginClassName} ${topMarginClassName}`
  })

  /**
   * CSS class that determines the container's vertical padding.
   */
  const paddingClassName = computed<string>(() => {
    if (!props.padding) {
      return ''
    }

    const bottomPaddingClassName: string =
      containerPaddingClassNameRegistry[props.padding.bottom].bottom

    const topPaddingClassName: string =
      containerPaddingClassNameRegistry[props.padding.top].top

    return `${bottomPaddingClassName} ${topPaddingClassName}`
  })

  const widthClassName = computed<string>(() => {
    if (!(props.width in containerWidthClassNameRegistry)) {
      return containerWidthClassNameRegistry[ContainerWidth.Normal]
    }

    return containerWidthClassNameRegistry[props.width]
  })

  const zIndexClassName = computed<string>(() => {
    if (!props.zIndex) {
      return ''
    }
    if (!(props.zIndex in containerZIndexClassNameRegistry)) {
      return ''
    }

    return containerZIndexClassNameRegistry[props.zIndex]
  })

  /**
   * CSS class that determines the container's vertical margin.
   */
  const backgroundVariantClass = useOptionClass(props.background.variantColor)

  return {
    bgImagePosition,
    fixedBackgroundStyles,
    hasBackgroundImage,
    hasFixedBackgroundImage,
    hasStyleDefined,
    marginClassName,
    paddingClassName,
    stylesDefinition,
    widthClassName,
    zIndexClassName,
    backgroundVariantClass
  }
}
